import React, { useState } from "react";
import { SortableContainer, SortableElement, arrayMove } from "react-sortable-hoc";
import axios from "axios";

// Sortable item (individual product row)
const SortableItem = SortableElement(({ product }) => (
  <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
    <td className="px-6 py-3">{product.title}</td>
    <td className="px-6 py-3">${product.selectedPricingValue}</td>
    <td className="px-6 py-3">{product.quantity}</td>
    <td className="px-6 py-3">
      ${product.quantity * product.selectedPricingValue}
    </td>
    <td className="px-6 py-3">{product.tax}</td>
    <td className="px-6 py-3">{product.discount || "0"}</td>
    <td className="px-6 py-3">
      ${product.quantity * product.selectedPricingValue - (product.discount || 0)}
    </td>
    <td className="px-6 py-3">Actions</td>
  </tr>
));

// Sortable list (the table body)
const SortableList = SortableContainer(({ items }) => (
  <tbody>
    {items.map((product, index) => (
      <SortableItem key={`item-${product.productId}`} index={index} product={product} />
    ))}
  </tbody>
));

// Fulfillment component
const Fulfillment = ({ products }) => {
  const [sets, setSets] = useState(products?.set || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const orderId = products._id;

  const handleSortEnd = async (setCode, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;

    const updatedSets = sets.map((perSet) => {
      if (perSet.setCode === setCode) {
        const updatedProducts = arrayMove(perSet.products, oldIndex, newIndex);
        return { ...perSet, products: updatedProducts };
      }
      return perSet;
    });

    setSets(updatedSets);
    setLoading(true);
    setError("");

    try {
      await axios.put(`https://server-gs.vercel.app/handle-sort/${orderId}`, {
        sets: updatedSets,
      });
    } catch (err) {
      setError("Failed to update sort order. Please try again.");
      console.error("Sort update failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {sets.length > 0 ? (
        sets.map((perSet) => (
          <div key={perSet.setCode} className="bg-white border rounded-lg w-[97%] mx-auto shadow-md">
            <div className="bg-blue-500 px-6 py-4 rounded-t-lg flex justify-between items-center">
              <div className="text-white">
                <p className="text-xl font-semibold">{perSet.setTitle}</p>
                <p className="text-sm">
                  Start: {new Date(perSet.startDate).toLocaleString()} - End:{" "}
                  {new Date(perSet.endDate).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left text-gray-600 dark:text-gray-400">
                <thead className="uppercase bg-gray-100 dark:bg-gray-700 text-xs text-gray-600">
                  <tr>
                    <th className="px-6 py-3">Item</th>
                    <th className="px-6 py-3">Price</th>
                    <th className="px-6 py-3">Quantity</th>
                    <th className="px-6 py-3">Total</th>
                    <th className="px-6 py-3">Tax</th>
                    <th className="px-6 py-3">Discount</th>
                    <th className="px-6 py-3">Sub Total</th>
                    <th className="px-6 py-3">Actions</th>
                  </tr>
                </thead>
                {perSet.products && perSet.products.length > 0 ? (
                  <SortableList
                    items={perSet.products}
                    onSortEnd={(params) => handleSortEnd(perSet.setCode, params)}
                    lockAxis="y"
                    useDragHandle={false}
                  />
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan="8" className="text-center px-6 py-4">
                        No products in this set.
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center text-gray-500">No sets available</div>
      )}

      {loading && <div className="text-center text-blue-500">Updating sort order...</div>}
      {error && <div className="text-center text-red-500">{error}</div>}
    </div>
  );
};

export default Fulfillment;
